import { Box, Paper, Popper, Typography } from "@mui/material";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import React from "react";
import Chart from "react-google-charts";
import {
    GoogleChartOptions,
    GoogleChartWrapper,
    GoogleViz,
} from "react-google-charts/dist/types";
import theme from "Theme/AppTheme";
import { defaultColorScheme } from "../Visualization/GraphColorSchemes";
import type {
    TimelineChartData,
    TimelineChartEntry,
} from "utils/AmdTimelineData";

const timelineChartAxisHeight = 72;

function getLogarithmicAxisTicks(minimum: number, maximum: number) {
    const firstExponent = Math.floor(Math.log10(minimum));
    const lastExponent = Math.ceil(Math.log10(maximum));

    return Array.from(
        { length: lastExponent - firstExponent + 1 },
        (_, index) => 10 ** (firstExponent + index),
    );
}

export function getRenderableTimelineData(
    data: TimelineChartData,
    minimumValue?: number,
): TimelineChartData {
    if (minimumValue === undefined) return data;

    return [
        data[0],
        ...(data.slice(1) as TimelineChartEntry[]).map(
            ([label, value, style]): TimelineChartEntry => [
                label,
                Math.max(value, minimumValue),
                style,
            ],
        ),
    ];
}

interface ChartHoverEvents {
    addListener(
        target: unknown,
        eventName: "onmouseover" | "onmouseout" | "ready",
        handler: (event: { row: number | null }) => void,
    ): void;
}

interface TimelineChartTooltip {
    label: string;
    duration: string;
    x: number;
    y: number;
}

export function formatChartDuration(totalSeconds: number) {
    if (totalSeconds <= 0) return "0s";
    if (totalSeconds < 0.01) return "< 0.01s";
    if (totalSeconds < 60) return `${Number(totalSeconds.toFixed(2))}s`;

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.round(totalSeconds % 60);
    if (minutes < 60) return `${minutes}m ${seconds}s`;

    return `${Math.floor(minutes / 60)}h ${minutes % 60}m ${seconds}s`;
}

type ChartProps = {
    data: TimelineChartData;
    options: Partial<GoogleChartOptions>;
    height: number;
    onReady?: (wrapper: GoogleChartWrapper, google: GoogleViz) => void;
};

// Keep chart instances stable while tooltip state changes.
const TimelineChart = React.memo(function TimelineChart({
    data,
    options,
    height,
    onReady,
}: ChartProps) {
    const wrapper = React.useRef<GoogleChartWrapper>();
    React.useEffect(() => () => wrapper.current?.getChart()?.clearChart(), []);
    const handleReady = React.useCallback(
        (chart: GoogleChartWrapper, google: GoogleViz) => {
            wrapper.current = chart;
            onReady?.(chart, google);
        },
        [onReady],
    );

    return (
        <Chart
            width="100%"
            height={`${height}px`}
            chartType="BarChart"
            data={data}
            options={options}
            getChartWrapper={handleReady}
            loader={
                <AcxLoadingIndicator
                    color="secondary"
                    alternate="PuffLoader"
                    size={96}
                    style={{
                        height: `${height}px`,
                        zIndex: theme.zIndex.appBar - 2,
                    }}
                />
            }
        />
    );
});

// Disjoint HorizontalBarGraph component to avoid shared dependency regressions
const AmdTimelineHorizontalBarGraph = React.memo(
    function AmdTimelineHorizontalBarGraph({
        data,
    }: {
        data: TimelineChartData;
    }) {
        const chartHeight = Math.max(140, (data.length - 1) * 30 + 48);
        const { renderData, barOptions, axisOptions } = React.useMemo(() => {
            const durations = data.slice(1).map((entry) => Number(entry[1]));
            const minimum = Math.min(...durations);
            const maximum = Math.max(...durations);
            const logarithmic =
                durations.length > 1 && minimum > 0 && minimum < maximum;
            const min = logarithmic ? (maximum >= 0.1 ? 0.1 : undefined) : 0;
            const max = logarithmic ? undefined : Math.max(1, maximum) * 1.25;
            const hAxis = {
                scaleType: logarithmic ? "log" : null,
                viewWindow:
                    min !== undefined || max !== undefined
                        ? { min, max }
                        : undefined,
                ticks: logarithmic
                    ? getLogarithmicAxisTicks(
                          Math.max(minimum, min ?? 0),
                          maximum,
                      )
                    : undefined,
                titleTextStyle: {
                    color: theme.palette.gray[400],
                    fontSize: 16,
                    fontName: "Inter Bold",
                },
                textStyle: {
                    color: theme.palette.gray[400],
                    fontSize: 13,
                    fontName: "Inter Bold",
                },
                gridlines: { color: "none" },
            };
            const options: Partial<GoogleChartOptions> = {
                fontName: "Inter",
                animation: { startup: false, duration: 0 },
                colors: defaultColorScheme,
                legend: { position: "none" },
                tooltip: { textStyle: { fontName: "Inter" }, isHtml: true },
                vAxis: {
                    textPosition: "in",
                    textStyle: {
                        color: theme.palette.black,
                        auraColor: "none",
                        fontName: "Inter Bold",
                        fontSize: 11,
                    },
                },
            };
            return {
                renderData: logarithmic
                    ? getRenderableTimelineData(data, min)
                    : data,
                barOptions: {
                    ...options,
                    focusTarget: "category",
                    dataOpacity: 0.65,
                    chartArea: {
                        width: "100%",
                        top: 16,
                        height: chartHeight - 32,
                    },
                    hAxis: { ...hAxis, textPosition: "none" },
                },
                axisOptions: {
                    ...options,
                    dataOpacity: 0,
                    // Leave room for the rightmost logarithmic tick.
                    chartArea: { width: "90%", top: 4, height: 1 },
                    vAxis: { ...options.vAxis, textPosition: "none" },
                    hAxis: { ...hAxis, title: "Time Spent (s)" },
                },
            };
        }, [data, chartHeight]);

        const [tooltip, setTooltip] = React.useState<TimelineChartTooltip>();
        const dataRef = React.useRef(data);
        const pointerPositionRef = React.useRef<{ x: number; y: number }>();
        const scrollContainerRef = React.useRef<HTMLDivElement>(null);
        const hoverRefreshFrameRef = React.useRef<number>();
        const hoverChartRef = React.useRef<unknown>(null);
        const hoverListenerTimeoutRef = React.useRef<number>();

        React.useEffect(() => {
            dataRef.current = data;
            setTooltip(undefined);
        }, [data]);

        React.useEffect(
            () => () => window.clearTimeout(hoverListenerTimeoutRef.current),
            [],
        );

        const clearHover = React.useCallback(() => {
            pointerPositionRef.current = undefined;
            setTooltip(undefined);
            if (hoverRefreshFrameRef.current !== undefined) {
                window.cancelAnimationFrame(hoverRefreshFrameRef.current);
                hoverRefreshFrameRef.current = undefined;
            }
        }, []);

        const refreshHoverTarget = React.useCallback(() => {
            if (
                !pointerPositionRef.current ||
                hoverRefreshFrameRef.current !== undefined
            )
                return;

            hoverRefreshFrameRef.current = window.requestAnimationFrame(() => {
                hoverRefreshFrameRef.current = undefined;

                const pointer = pointerPositionRef.current;
                if (!pointer) return;
                const { x, y } = pointer;
                const target = document.elementFromPoint(x, y);

                if (!target || !scrollContainerRef.current?.contains(target)) {
                    clearHover();
                    return;
                }

                target.dispatchEvent(
                    new MouseEvent("mousemove", {
                        bubbles: true,
                        clientX: x,
                        clientY: y,
                    }),
                );
            });
        }, [clearHover]);

        React.useEffect(() => {
            // Capture pointer before Google Charts handles the event
            const trackPointer = (event: MouseEvent) => {
                if (
                    event.target instanceof Node &&
                    scrollContainerRef.current?.contains(event.target)
                ) {
                    pointerPositionRef.current = {
                        x: event.clientX,
                        y: event.clientY,
                    };
                } else {
                    clearHover();
                }
            };
            window.addEventListener("mousemove", trackPointer, true);
            window.addEventListener("mouseover", trackPointer, true);
            window.addEventListener("blur", clearHover);
            window.addEventListener("scroll", refreshHoverTarget, true);

            return () => {
                window.removeEventListener("mousemove", trackPointer, true);
                window.removeEventListener("mouseover", trackPointer, true);
                window.removeEventListener("blur", clearHover);
                window.removeEventListener("scroll", refreshHoverTarget, true);
                if (hoverRefreshFrameRef.current !== undefined) {
                    window.cancelAnimationFrame(hoverRefreshFrameRef.current);
                }
            };
        }, [clearHover, refreshHoverTarget]);

        const handleChartReady = React.useCallback(
            (chartWrapper: GoogleChartWrapper, google: GoogleViz) => {
                const events = google.visualization
                    .events as unknown as ChartHoverEvents;

                hoverListenerTimeoutRef.current = window.setTimeout(() => {
                    const listenForHover = () => {
                        const chart = chartWrapper.getChart();
                        if (!chart || hoverChartRef.current === chart) return;
                        hoverChartRef.current = chart;

                        events.addListener(
                            chart,
                            "onmouseover",
                            ({ row }: { row: number | null }) => {
                                const pointer = pointerPositionRef.current;
                                const entry =
                                    row === null
                                        ? undefined
                                        : dataRef.current[row + 1];

                                if (
                                    !pointer ||
                                    !entry ||
                                    typeof entry[1] !== "number"
                                ) {
                                    setTooltip(undefined);
                                    return;
                                }

                                setTooltip({
                                    label: String(entry[0]),
                                    duration: formatChartDuration(entry[1]),
                                    ...pointer,
                                });
                            },
                        );
                        events.addListener(chart, "onmouseout", () =>
                            setTooltip(undefined),
                        );
                    };

                    listenForHover();
                    events.addListener(chartWrapper, "ready", listenForHover);
                });
            },
            [],
        );

        const tooltipAnchor = React.useMemo(() => {
            if (!tooltip) return null;
            return {
                getBoundingClientRect: () => new DOMRect(tooltip.x, tooltip.y),
            };
        }, [tooltip]);

        return (
            <Paper
                variant="outlined"
                sx={{
                    height: chartHeight + timelineChartAxisHeight + 34,
                    maxHeight: { xs: "60dvh", lg: "75dvh" },
                    position: { lg: "sticky" },
                    top: { lg: "1rem" },
                    display: "flex",
                    flexDirection: "column",
                    p: 2,
                    overflowX: "hidden",
                    overflowY: "hidden",
                    borderColor: theme.palette.gray[200],
                    borderRadius: 2,
                }}
            >
                <Box
                    ref={scrollContainerRef}
                    onMouseLeave={clearHover}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: "auto",
                        "& .google-visualization-tooltip": {
                            display: "none !important",
                        },
                    }}
                >
                    <TimelineChart
                        key={chartHeight}
                        height={chartHeight}
                        data={renderData}
                        options={barOptions}
                        onReady={handleChartReady}
                    />
                </Box>
                <Box
                    sx={{
                        height: timelineChartAxisHeight,
                        flexShrink: 0,
                        pointerEvents: "none",
                    }}
                >
                    <TimelineChart
                        height={timelineChartAxisHeight}
                        data={renderData}
                        options={axisOptions}
                    />
                </Box>
                <Popper
                    open={Boolean(tooltip)}
                    anchorEl={tooltipAnchor}
                    placement="right-start"
                    modifiers={[
                        { name: "offset", options: { offset: [0, 12] } },
                        { name: "preventOverflow", options: { padding: 8 } },
                        { name: "flip", options: { padding: 8 } },
                    ]}
                    sx={{
                        pointerEvents: "none",
                        zIndex: theme.zIndex.tooltip,
                    }}
                >
                    <Paper
                        elevation={3}
                        sx={{
                            // Grow to fit the label instead of wrapping it
                            width: "max-content",
                            maxWidth: "min(640px, calc(100vw - 32px))",
                            boxSizing: "border-box",
                            overflowWrap: "anywhere",
                            px: 1.25,
                            py: 0.75,
                            border: `1px solid ${theme.palette.gray[200]}`,
                            borderRadius: 1,
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600 }}
                        >
                            {tooltip?.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {`Time Spent: ${tooltip?.duration}`}
                        </Typography>
                    </Paper>
                </Popper>
            </Paper>
        );
    },
);

export default AmdTimelineHorizontalBarGraph;
